import React, { useMemo } from "react";
import {
  COMMERCIAL_DOMAINS,
  COMMERCIAL_DOMAIN_SCHEMAS,
  commercialFieldVisible,
} from "./commercialProjectSchema";
import CommercialFieldFileDisplay, { CommercialFileList } from "./CommercialFieldFileDisplay";
import { collectCommercialFileEntries } from "./commercialProjectFiles";
import { repeaterToArray } from "./commercialProjectAcf";
import { normalizeMulti, serviceSpeedLabel } from "./commercialProjectSchema";
import PermitsSectionPanel, {
  ConstructionPermitsReadPanel,
  ExpansionPermitCoverageReadPanel,
} from "./PermitsSectionPanel";
import { PERMIT_EDIT_CONTEXT } from "./commercialPermitsSchema";
import { formatSubphaseLabel, readSubphaseForCurrentPhase } from "./commercialSubphases";
import { departmentIncludesFiber } from "./commercialProjectSchema";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import { overlayContractDerivedConstruction } from "./contractsDerivedFields";
import { formatContractCost } from "./contractsAccess";
import { CdtInterconnectReadPanel } from "./CdtInterconnectPanel";
import { EXPANSION_SUMMARY_DOMAIN } from "./expansionProjectSchema";
import { expansionProjectHasCdt } from "./interconnectsUtils";
import { formatDateOnlyDisplay } from "./dateOnlyDisplay";
import { displayNameForMaterial } from "./engineeringMaterialsCatalog";
import { viewerIsContractor } from "./contractorProjectAccess";

function isFileField(type) {
  return type === "file" || type === "multifile";
}

function formatReadValue(field, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (field.type === "bool") return value ? "Yes" : "No";
  if (field.type === "currency" || field.key === "contract_cost") {
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return formatContractCost(n);
  }
  if (field.type === "date") {
    if (value == null || String(value).trim() === "") return "—";
    const formatted = formatDateOnlyDisplay(value);
    return formatted || String(value);
  }
  if (field.type === "multiselect" || field.type === "user_role_multiselect") {
    const arr = normalizeMulti(value);
    return arr.length ? arr.join(", ") : "—";
  }
  if (field.key === "service_speed") {
    const label = serviceSpeedLabel(value);
    return label || "—";
  }
  if (field.type === "material_catalog") {
    const label = displayNameForMaterial(value);
    return label || "—";
  }
  if (Array.isArray(value)) {
    if (!value.length) return "—";
    if (value.every((x) => x == null || typeof x !== "object")) {
      return value.map((x) => String(x ?? "").trim()).filter(Boolean).join(", ");
    }
    return "—";
  }
  if (typeof value === "object") return "—";
  return String(value);
}

function ReadNestedRepeaterTable({ field, rows }) {
  const arr = repeaterToArray(rows);
  if (!arr.length) return null;
  const cols = (field.fields || []).filter((c) => c.type !== "file" && c.type !== "multifile");
  if (!cols.length) return null;
  return (
    <table className="commercial-materials-table">
      <thead>
        <tr>
          {cols.map((c) => (
            <th key={c.key}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {arr.map((row, i) => (
          <tr key={i}>
            {cols.map((c) => {
              const raw = row?.[c.key];
              const display =
                c.type === "material_catalog" || c.key === "item"
                  ? displayNameForMaterial(row?.slug || raw)
                  : formatReadValue(c, raw);
              return <td key={c.key}>{display}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReadFieldRow({ label, children }) {
  return (
    <div className="commercial-read-row">
      <div className="commercial-read-label">{label}</div>
      <div className="commercial-read-value">{children}</div>
    </div>
  );
}

function ReadRepeaterBlock({ field, rows, ctx, domainKey }) {
  const arr = repeaterToArray(rows);
  if (arr.length === 0) return null;
  return (
    <div className="commercial-read-repeater">
      <h4 className="commercial-read-repeater-title">{field.label}</h4>
      {arr.map((row, idx) => (
        <div key={idx} className="commercial-read-repeater-card">
          <div className="commercial-read-repeater-head">
            {field.rowLabel || "Row"} {idx + 1}
          </div>
          {(field.fields || []).map((sub) => {
            if (!commercialFieldVisible(sub, ctx)) return null;
            const val = row?.[sub.key];
            if (sub.type === "file") {
              const url =
                typeof val === "string"
                  ? val.trim()
                  : val && typeof val === "object"
                    ? String(val.url || "").trim()
                    : "";
              if (!url) return null;
              return (
                <ReadFieldRow key={sub.key} label={sub.label}>
                  <CommercialFieldFileDisplay value={val} fieldKey={sub.key} accept={sub.accept} />
                </ReadFieldRow>
              );
            }
            if (sub.type === "multifile") return null;
            if (sub.type === "repeater") {
              const nested = repeaterToArray(val);
              if (!nested.length) return null;
              return (
                <ReadFieldRow key={sub.key} label={sub.label}>
                  <ReadNestedRepeaterTable field={sub} rows={val} />
                </ReadFieldRow>
              );
            }
            return (
              <ReadFieldRow key={sub.key} label={sub.label}>
                {formatReadValue(sub, val)}
              </ReadFieldRow>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Fiber OTDR/DLR files from the Fiber domain, shown read-only on the Networking tab. */
export function FiberFilesReadOnlySection({
  domains,
  schemas = COMMERCIAL_DOMAIN_SCHEMAS,
  summaryDomainKey = COMMERCIAL_DOMAINS.SUMMARY,
}) {
  const fiberSchema = schemas[COMMERCIAL_DOMAINS.FIBER];
  const fiberField = fiberSchema?.fields?.find((f) => f.key === "fiber_files");

  const ctx = useMemo(
    () => ({
      summary: domains?.[summaryDomainKey] || {},
      networking: domains?.[COMMERCIAL_DOMAINS.NETWORKING] || {},
    }),
    [domains, summaryDomainKey]
  );

  if (!fiberField) return null;

  const fiberDeptApplies = fiberSchema.sectionVisible
    ? fiberSchema.sectionVisible(ctx)
    : departmentIncludesFiber(ctx.summary);
  const rows = repeaterToArray(domains?.[COMMERCIAL_DOMAINS.FIBER]?.fiber_files);
  if (!fiberDeptApplies && rows.length === 0) return null;

  return (
    <div className="commercial-read-fiber-files" style={{ marginTop: 20 }}>
      <h4 className="commercial-read-repeater-title">{fiberField.label}</h4>
      {rows.length === 0 ? (
        <p className="project-detail-muted">No fiber files yet.</p>
      ) : (
        <ReadRepeaterBlock
          field={fiberField}
          rows={rows}
          ctx={ctx}
          domainKey={COMMERCIAL_DOMAINS.FIBER}
        />
      )}
    </div>
  );
}

export default function CommercialSectionReadView({
  domainKey,
  domains,
  filesOnly = false,
  schemas = COMMERCIAL_DOMAIN_SCHEMAS,
  summaryDomainKey = COMMERCIAL_DOMAINS.SUMMARY,
  contractDerivedConstruction,
  interconnectFields,
  projectRow,
  showLinkedFiberFiles = false,
  projectId,
  permitCoverage = null,
  apiUrl,
  getAuthHeaders,
}) {
  const schema = schemas[domainKey];
  const mergedDomains = useMemo(() => {
    if (!contractDerivedConstruction?.fields) return domains;
    return overlayContractDerivedConstruction(domains, contractDerivedConstruction.fields);
  }, [domains, contractDerivedConstruction]);
  const payload = mergedDomains?.[domainKey];

  const viewerEmail = getViewerEmailFromStorage();
  const ctx = useMemo(
    () => ({
      summary: mergedDomains?.[summaryDomainKey] || {},
      networking: mergedDomains?.[COMMERCIAL_DOMAINS.NETWORKING] || {},
      viewerEmail,
    }),
    [mergedDomains, summaryDomainKey, viewerEmail]
  );

  if (!schema) return null;

  const engineeringPermits = mergedDomains?.[COMMERCIAL_DOMAINS.ENGINEERING]?.permits;
  const contractorViewer = viewerIsContractor(viewerEmail);
  if (contractorViewer && domainKey === COMMERCIAL_DOMAINS.CONSTRUCTION) {
    return (
      <div className="commercial-tab-read-panel project-detail-tab-content">
        <ConstructionPermitsReadPanel permits={engineeringPermits} />
      </div>
    );
  }

  if (schema.sectionVisible && !schema.sectionVisible(ctx)) {
    return (
      <div className="commercial-tab-read-panel project-detail-tab-content">
        <p className="project-detail-muted">
          Enable <strong>{schema.label}</strong> by selecting the matching department in Project summary.
        </p>
      </div>
    );
  }

  const fileEntries = collectCommercialFileEntries(mergedDomains, domainKey, ctx);

  if (filesOnly) {
    if (fileEntries.length === 0) return null;
    return (
      <div className="commercial-tab-read-panel project-detail-tab-content commercial-documents-panel">
        <h3 className="commercial-section-title">Documents</h3>
        <CommercialFileList entries={fileEntries} />
      </div>
    );
  }

  const showConstructionPermits = domainKey === COMMERCIAL_DOMAINS.CONSTRUCTION;
  const showPermitCoverage =
    !contractorViewer &&
    summaryDomainKey === EXPANSION_SUMMARY_DOMAIN &&
    (domainKey === COMMERCIAL_DOMAINS.ENGINEERING || domainKey === COMMERCIAL_DOMAINS.CONSTRUCTION);
  const permitCoverageConfirmation =
    mergedDomains?.[COMMERCIAL_DOMAINS.ENGINEERING]?.permit_coverage_confirmation;

  const hasScalar =
    payload &&
    typeof payload === "object" &&
    (schema.fields || []).some((field) => {
      if (field.type === "repeater" || isFileField(field.type)) return false;
      if (!commercialFieldVisible(field, ctx)) return false;
      const storageDomainKey = field.valueDomain || domainKey;
      const storagePayload =
        storageDomainKey === domainKey ? payload : mergedDomains?.[storageDomainKey];
      const v = storagePayload?.[field.key];
      return v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0);
    });

  const hasFiles = fileEntries.length > 0;
  const hasRepeaters = (schema.fields || []).some(
    (f) => f.type === "repeater" && repeaterToArray(payload?.[f.key]).length > 0
  );

  const hasInterconnectPanel =
    domainKey === COMMERCIAL_DOMAINS.ENGINEERING &&
    summaryDomainKey === EXPANSION_SUMMARY_DOMAIN &&
    expansionProjectHasCdt(projectRow) &&
    interconnectFields?.fields;

  if (
    !hasScalar &&
    !hasFiles &&
    !hasRepeaters &&
    !showConstructionPermits &&
    !hasInterconnectPanel &&
    !showPermitCoverage
  ) {
    return (
      <div className="commercial-tab-read-panel project-detail-tab-content">
        <p className="project-detail-muted">No data in this section yet.</p>
      </div>
    );
  }

  return (
    <div className="commercial-tab-read-panel project-detail-tab-content">
      <h3 className="commercial-section-title">{schema.label}</h3>
      {showPermitCoverage ? (
        <ExpansionPermitCoverageReadPanel
          projectId={projectId}
          permitCoverage={permitCoverage}
          confirmationValue={permitCoverageConfirmation}
          apiUrl={apiUrl}
          getAuthHeaders={getAuthHeaders}
        />
      ) : null}
      {domainKey === COMMERCIAL_DOMAINS.ENGINEERING &&
      summaryDomainKey === EXPANSION_SUMMARY_DOMAIN &&
      expansionProjectHasCdt(projectRow) ? (
        <CdtInterconnectReadPanel interconnectFields={interconnectFields} projectRow={projectRow} />
      ) : null}
      <div className="commercial-read-fields">
        {(schema.fields || []).map((field) => {
          if (!commercialFieldVisible(field, ctx)) return null;
          const storageDomainKey = field.valueDomain || domainKey;
          const storagePayload =
            storageDomainKey === domainKey ? payload : mergedDomains?.[storageDomainKey];
          const val = storagePayload?.[field.key];
          if (field.type === "file") {
            const url =
              typeof val === "string"
                ? val.trim()
                : val && typeof val === "object"
                  ? String(val.url || "").trim()
                  : "";
            if (!url) return null;
            return (
              <ReadFieldRow key={field.key} label={field.label}>
                <CommercialFieldFileDisplay value={val} fieldKey={field.key} accept={field.accept} />
              </ReadFieldRow>
            );
          }
          if (field.type === "multifile") {
            const rows = Array.isArray(val) ? val : [];
            const hasAny = rows.some((row) => {
              const u =
                typeof row === "string"
                  ? row.trim()
                  : row && typeof row === "object"
                    ? String(row.site_walk_file || row.url || "").trim()
                    : "";
              return Boolean(u);
            });
            if (!hasAny) return null;
            return (
              <ReadFieldRow key={field.key} label={field.label}>
                <ul className="commercial-file-list">
                  {(Array.isArray(val) ? val : []).map((row, i) => {
                    const url =
                      typeof row === "string"
                        ? row.trim()
                        : row && typeof row === "object"
                          ? String(row.site_walk_file || row.url || "").trim()
                          : "";
                    if (!url) return null;
                    return (
                      <li key={i} className="commercial-file-list-item">
                        <CommercialFieldFileDisplay
                          value={row}
                          fieldKey={field.key}
                          accept={field.accept}
                        />
                      </li>
                    );
                  })}
                </ul>
              </ReadFieldRow>
            );
          }
          if (field.type === "fiber_az_otdr") {
            const files = val && typeof val === "object" && !Array.isArray(val) ? val : {};
            const parts = ["A", "Z"].map((loc) => {
              const otdr = files[loc]?.otdr;
              if (otdr == null || String(otdr).trim() === "") return null;
              return `${loc}: OTDR uploaded`;
            }).filter(Boolean);
            if (!parts.length) return null;
            return (
              <ReadFieldRow key={field.key} label={field.label}>
                {parts.join(" · ")}
              </ReadFieldRow>
            );
          }
          if (field.type === "repeater") {
            if (field.key === "permits") {
              return (
                <PermitsSectionPanel
                  key={field.key}
                  title={field.label}
                  permits={val}
                  editContext={PERMIT_EDIT_CONTEXT.ENGINEERING}
                />
              );
            }
            return (
              <ReadRepeaterBlock
                key={field.key}
                field={field}
                rows={val}
                ctx={ctx}
                domainKey={domainKey}
              />
            );
          }
          if (field.computed || field.type === "readonly") {
            return (
              <ReadFieldRow key={field.key} label={field.label}>
                {formatReadValue(field, val)}
              </ReadFieldRow>
            );
          }
          if (field.key === "subphase" && domainKey === summaryDomainKey) {
            const slug = readSubphaseForCurrentPhase(payload);
            const label = slug ? formatSubphaseLabel(slug) : "—";
            return (
              <ReadFieldRow key={field.key} label={field.label}>
                {label}
              </ReadFieldRow>
            );
          }
          return (
            <ReadFieldRow key={field.key} label={field.label}>
              {formatReadValue(field, val)}
            </ReadFieldRow>
          );
        })}
      </div>
      {showConstructionPermits ? (
        <ConstructionPermitsReadPanel permits={engineeringPermits} />
      ) : null}
      {showLinkedFiberFiles && domainKey === COMMERCIAL_DOMAINS.NETWORKING ? (
        <FiberFilesReadOnlySection
          domains={mergedDomains}
          schemas={schemas}
          summaryDomainKey={summaryDomainKey}
        />
      ) : null}
    </div>
  );
}
